import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { FaHistory } from 'react-icons/fa';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';

const BID_COLOR = '#008f4f';
const ASK_COLOR = '#d92d20';
const MAX_VISIBLE_DEPTH_ROWS = 5;

const Container = styled.div`
  height: 100%;

  > .top {
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.16), transparent 42%),
      ${PRIMARY};
    padding: 8px 12px;
    font-family: Poppins-B;
    text-transform: uppercase;
    display: flex;
    justify-content: space-between;
    font-size: 15px;
  }

  > .content {
    height: calc(100% - 38px);
    box-sizing: border-box;
    padding: 12px;
    font-family: Poppins-R;
    overflow-x: auto;
    color: var(--mt-text);

    > p {
      text-align: center;
    }
  }
`;

export const GraphData = ({ marketData }: { marketData: any[] }) => {
  const [page, setPage] = useState('1');

  const changeContent = () => {
    if (page === '1') setPage('2');
    else setPage('1');
  };

  return (
    <Container>
      <div className='top'>
        <div style={{ color: 'var(--mt-warning)' }}>
          {page === '1'
            ? 'Profondeur du marché'
            : 'Historique graphique des données'}
        </div>
        <div>
          <Tooltip
            title={page === '1' ? 'Données graphique' : 'Profondeur du marché'}
            color={YELLOW}
            placement='rightBottom'
          >
            <FaHistory
              style={{ cursor: 'pointer' }}
              size={18}
              onClick={changeContent}
            />
          </Tooltip>
        </div>
      </div>

      <div className='content'>
        {page === '1' ? (
          <Profondeur marketData={marketData} />
        ) : (
          <p>Aucune donnée n'est disponible</p>
        )}
      </div>
    </Container>
  );
};

const ProfContainer = styled.div`
  font-family: Poppins-R;

  .depth-table-wrap {
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-card);
  }

  .depth-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .depth-summary strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .depth-summary span {
    padding: 3px 8px;
    border: 1px solid rgba(255, 176, 0, 0.35);
    border-radius: 999px;
    color: var(--mt-warning);
    font-family: Poppins-B;
  }

  .empty-depth {
    min-height: 190px;
    display: grid;
    place-items: center;
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background:
      linear-gradient(135deg, rgba(255, 176, 0, 0.08), transparent 55%),
      var(--mt-surface);
    text-align: center;
  }

  .empty-depth strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 14px;
  }

  .empty-depth span {
    display: block;
    margin-top: 4px;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .top-line > td {
    text-align: center;
    color: var(--mt-muted);
    font-size: 11px;
    font-style: italic;
    background-color: var(--mt-surface);
  }

  .body > td {
    padding: 6px 8px;
    font-size: 12px;
    text-align: right;
    border-top: 1px solid var(--mt-border);
  }

  .body:nth-of-type(even) > td {
    background-color: rgba(148, 163, 184, 0.06);
  }

  .buy-cell {
    color: ${BID_COLOR};
    font-family: Poppins-B;
  }

  .sell-cell {
    color: ${ASK_COLOR};
    font-family: Poppins-B;
  }

  table {
    width: 100%;
    min-width: 520px;
    border-collapse: separate;
    border-spacing: 0;
  }

  thead {
    background-color: ${PRIMARY};
  }

  th {
    padding: 6px 8px;
    color: white;
    font-size: 12px;
    font-family: Poppins-B;
  }

  th:first-of-type {
    color: ${BID_COLOR};
  }

  th:last-of-type {
    color: ${ASK_COLOR};
  }
`;

const Profondeur = ({ marketData }: { marketData: any[] }) => {
  const daysSince = (value: any) =>
    value === '--' ? '--' : moment(new Date()).diff(moment(value), 'days') || 0;
  const visibleDepth = marketData.slice(0, MAX_VISIBLE_DEPTH_ROWS);
  const hiddenRows = Math.max(marketData.length - visibleDepth.length, 0);

  if (!marketData.length) {
    return (
      <ProfContainer>
        <div className='empty-depth'>
          <div>
            <strong>Aucune profondeur disponible</strong>
            <span>Les meilleurs ordres s'afficheront ici dès réception.</span>
          </div>
        </div>
      </ProfContainer>
    );
  }

  return (
    <ProfContainer>
      <div className='depth-summary'>
        <strong>Meilleurs niveaux du carnet</strong>
        <span>
          {visibleDepth.length}/{marketData.length} affichés
        </span>
      </div>
      <div className='depth-table-wrap'>
        <table>
          <thead>
            <tr>
              <th colSpan={3}>Achats</th>
              <th colSpan={3}>Ventes</th>
            </tr>
          </thead>
          <tbody>
            <tr className='top-line'>
              <td>Nb Jours</td>
              <td>Qte achat</td>
              <td>Prix achat</td>
              <td>Prix vente</td>
              <td>Qte vente</td>
              <td>Nb Jours</td>
            </tr>
            {visibleDepth.map((val, index) => (
              <tr key={index} className='body'>
                <td>{daysSince(val.dateAchat)}</td>
                <td className='buy-cell'>{formatNum(val.qteAchat)}</td>
                <td className='buy-cell'>{formatNum(val.prixAchat)}</td>
                <td className='sell-cell'>{formatNum(val.prixVente)}</td>
                <td className='sell-cell'>{formatNum(val.qteVente)}</td>
                <td>{daysSince(val.dateVente)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hiddenRows > 0 && (
        <div className='depth-summary' style={{ marginTop: 8, marginBottom: 0 }}>
          <span>{hiddenRows} niveaux masqués</span>
          <strong>Vue complète disponible dans le carnet détaillé</strong>
        </div>
      )}
    </ProfContainer>
  );
};
